// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
'use client'

import { submitForm } from 'app/contact/actions.client'
import { ContactFormValues } from 'app/contact/ContactFormValues'
import ContactPageVM from 'clients/strapi/models/ContactPageVM'
import AnimatedHeading from 'components/AnimatedHeading/AnimatedHeading'
import Button from 'components/Buttons/Button/Button'
import Dropdown from 'components/Form/Dropdown/Dropdown'
import DropzoneInput from 'components/Form/DropzoneInput/DropzoneInput'
import Input from 'components/Form/Input/Input'
import Textarea from 'components/Form/Textarea/Textarea'
import TurnstileButton from 'components/FormSections/TurnstileButton/TurnstileButton'
import TransitionEnter from 'components/TransitionEnter/TransitionEnter'
import { emailRegex, phoneRegex } from 'constants/global'
import { motion } from 'motion/react'
import Mailbox from 'public/assets/mailbox.svg'
import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import styles from './ContactPage.module.scss'

type Props = {
  viewModel: ContactPageVM
}

const companyOptions = ['Startup', 'Small business', 'Enterprise', 'Agency', 'Recruiter', 'Freelancer']
const budgetOptions = ['<= 50.000€', '50.000€ - 150.000€', '150.000€ - 300.000€', '> 300.000€', "I don't know / I can't disclose"]
const helpOptions = [
  'I want to build a new product',
  'I want to hire a team to augment an existing one',
  'I want to outsource development of my product',
  "I'm not sure / I don't want to disclose yet",
]

const ContactPage = ({ viewModel }: Props) => {
  const [isLoading, setIsLoading] = useState(false)
  const [turnstileFailed, setTurnstileFailed] = useState(false)
  const { handleSubmit, setValue, control } = useForm<ContactFormValues>()
  const [formSubmitted, setFormSubmitted] = useState(false)

  const onSubmit = useCallback(
    async (data: ContactFormValues) => {
      setIsLoading(true)
      const res = await submitForm(data)
      setIsLoading(false)

      if (res) {
        const anchor = document.getElementById('scroll-anchor')?.getBoundingClientRect()
        window.scrollBy(0, anchor?.bottom)
        setFormSubmitted(true)
      }
    },
    [setIsLoading, setFormSubmitted]
  )

  return (
    <motion.div layoutRoot layout className={styles.container}>
      <TransitionEnter color='#6830F7'></TransitionEnter>

      <section id='scroll-anchor' className={styles['contact-header']}>
        <span className={styles.subtitle}>Get in touch!</span>
        <AnimatedHeading title={viewModel.title} page='contact' />
      </section>

      {!formSubmitted ? (
        <form className={styles['contact-form']} onSubmit={handleSubmit(onSubmit)}>
          <section className={styles['form-section']}>
            <span className={styles['section-title']}>Tell us about you</span>
            <div className={styles.inputs}>
              <Input
                label='First name *'
                type='text'
                control={control}
                name='name'
                rules={{
                  required: {
                    value: true,
                    message: 'Please fill in your first name.',
                  },
                }}
              />
              <Input
                label='Last name *'
                type='text'
                control={control}
                name='surname'
                rules={{
                  required: {
                    value: true,
                    message: 'Please fill in your last name.',
                  },
                }}
              />
              <Input
                label='Business email *'
                type='email'
                control={control}
                name='email'
                rules={{
                  required: {
                    value: true,
                    message: 'Please fill in your business email.',
                  },
                  pattern: {
                    value: emailRegex,
                    message: 'E-mail must be formatted correctly.',
                  },
                }}
              />
              <Input
                label='Phone number'
                type='text'
                control={control}
                name='phoneNumber'
                rules={{
                  pattern: {
                    value: phoneRegex,
                    message: 'Phone number must be formatted correctly.',
                  },
                }}
              />
              <Dropdown label='What is your company?' control={control} name='company' options={companyOptions} />
            </div>
          </section>

          <section className={styles['form-section']}>
            <span className={styles['section-title']}>Tell us about your project</span>
            <div className={styles.inputs}>
              <Dropdown label='How can we help?' control={control} name='interest' options={helpOptions} />
              <Dropdown label='What is your budget?' control={control} name='budget' options={budgetOptions} />
              <Textarea
                label='Tell us about your project *'
                maxCharacters={1000}
                control={control}
                name='about'
                rules={{
                  required: {
                    value: true,
                    message: 'Please tell us something about your project.',
                  },
                  maxLength: {
                    value: 1000,
                    message: '1000 characters max.',
                  },
                }}
              />
              <DropzoneInput
                name='files'
                multiple={true}
                placeholder='your CV, portfolio, and any other documents you feel is relevant'
                maxFiles={1}
                control={control}
              />
              <TurnstileButton
                control={control}
                rules={{
                  required: true,
                  validate: (value: string) => value.length > 0,
                }}
                setValue={setValue}
                hasFailed={setTurnstileFailed}
              />
              <Button type='submit' disabled={isLoading || turnstileFailed}>
                Submit
              </Button>
            </div>
          </section>
        </form>
      ) : (
        <motion.div
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3, ease: 'linear' }}
          className={styles['form-submitted']}
        >
          <Mailbox />
          <div className={styles['success-text']}>
            <span className={styles.big}>Thanks for reaching out!</span>
            <span className={styles.small}>
              We&apos;re excited to learn more about what you need. We&apos;ll review everything thoroughly, and if we&apos;re the right fit, we&apos;ll reach
              out shortly.
            </span>
          </div>
        </motion.div>
      )}
    </motion.div>
  )
}

export default ContactPage
